'use client';

import React, { useEffect, useRef } from 'react';

const steps = [
  'Not Just a Trek.',
  "A Journey You'll Remember Forever.",
  'Where noise fades away.',
  'No notifications. No deadlines.',
  'Just you, your breath, the mountain.',
];

const FADE_DURATION = 0.5;

export default function StorySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const textRef = useRef<HTMLHeadingElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    let ctx: { revert: () => void } | undefined;

    const init = async () => {
      const { gsap } = await import('gsap');
      const { ScrollTrigger } = await import('gsap/ScrollTrigger');
      gsap.registerPlugin(ScrollTrigger);

      ctx = gsap.context(() => {
        const totalSteps = steps.length;

        // 🔒 PIN SECTION
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: 'top top',
          end: `+=${totalSteps * 120}%`,
          pin: true,
          scrub: 1,
        });

        let currentIndex = -1;

        // 🎯 CONTROL TEXT
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: 'top top',
          end: `+=${totalSteps * 120}%`,
          scrub: true,
          onUpdate: (self) => {
            const progress = self.progress;

            const index = Math.min(
              totalSteps - 1,
              Math.floor(progress * totalSteps)
            );

            if (index !== currentIndex) {
              currentIndex = index;

              gsap.to(textRef.current, {
                opacity: 0,
                y: 20,
                duration: 0.25,
                onComplete: () => {
                  if (textRef.current) {
                    textRef.current.innerText = steps[index];
                  }

                  gsap.to(textRef.current, {
                    opacity: 1,
                    y: 0,
                    duration: 0.4,
                  });
                },
              });
            }
          },
        });
      }, sectionRef);
    };

    init();
    return () => ctx?.revert();
  }, []);

  // 🎬 VIDEO FADE (same as HeroSection)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tick = () => {
      if (!video.duration || isNaN(video.duration)) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const t = video.currentTime;
      const d = video.duration;

      if (t < FADE_DURATION) {
        video.style.opacity = String(t / FADE_DURATION);
      } else if (t > d - FADE_DURATION) {
        video.style.opacity = String(Math.max(0, (d - t) / FADE_DURATION));
      } else {
        video.style.opacity = '1';
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <section data-component-id="src/app/components/StorySection.tsx:116:4" data-component-path="src/app/components/StorySection.tsx" data-component-line="116" data-component-end-line="155" data-component-file="StorySection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22relative%20w-full%20h-screen%20overflow-hidden%20bg-white%22%7D"
      ref={sectionRef}
      className="relative w-full h-screen overflow-hidden bg-white"
    >
      {/* 🎬 VIDEO */}
      <div data-component-id="src/app/components/StorySection.tsx:121:6" data-component-path="src/app/components/StorySection.tsx" data-component-line="121" data-component-end-line="144" data-component-file="StorySection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20-z-10%20overflow-hidden%22%7D" className="absolute inset-0 -z-10 overflow-hidden">
        <video data-component-id="src/app/components/StorySection.tsx:122:8" data-component-path="src/app/components/StorySection.tsx" data-component-line="122" data-component-end-line="134" data-component-file="StorySection.tsx" data-component-name="video" data-component-content="%7B%22elementName%22%3A%22video%22%2C%22className%22%3A%22absolute%20inset-0%20w-full%20h-full%20object-cover%20object-center%22%2C%22src%22%3A%22%2Fassets%2Fvideos%2Fman-walking.mp4%22%7D"
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover object-center"
          src="/assets/videos/man-walking.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          style={{
            transform: 'scale(1.02)',
          }}
        />

        {/* ✅ WHITE GRADIENT OVERLAY */}
        <div data-component-id="src/app/components/StorySection.tsx:137:8" data-component-path="src/app/components/StorySection.tsx" data-component-line="137" data-component-end-line="143" data-component-file="StorySection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20pointer-events-none%22%7D"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(to bottom, #ffffff 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 60%, #ffffff 100%)',
          }}
        />
      </div>

      {/* ✨ TEXT */}
      <div data-component-id="src/app/components/StorySection.tsx:147:6" data-component-path="src/app/components/StorySection.tsx" data-component-line="147" data-component-end-line="154" data-component-file="StorySection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%20h-full%20flex%20items-center%20justify-center%20px-6%22%7D" className="relative z-10 h-full flex items-center justify-center px-6">
        <h2 data-component-id="src/app/components/StorySection.tsx:148:8" data-component-path="src/app/components/StorySection.tsx" data-component-line="148" data-component-end-line="153" data-component-file="StorySection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-center%20text-black%20font-bold%20text-4xl%20md%3Atext-6xl%20max-w-4xl%20leading-tight%22%7D"
          ref={textRef}
          className="text-center text-black font-bold text-4xl md:text-6xl max-w-4xl leading-tight"
        >
          {steps[0]}
        </h2>
      </div>
    </section>
  );
}